/**
 * One vehicle: who is driving it, its papers, and everyone who has had it.
 *
 * Giving the van to someone who already has someone else in it is one action — a
 * handover — because that is how it happens in the yard. Taking it back asks for the
 * km reading, which is the one number the office needs later for the fuel card and the
 * service interval.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Combobox } from '@/components/Combobox';
import { Modal } from '@/components/Modal';
import { Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { personOptions } from '@/features/access/people';
import { usePermissions } from '@/features/access/usePermissions';
import { RegisterPanel } from '@/features/documents/RegisterPanel';
import { VehicleForm } from '@/features/vehicles/VehicleForm';
import { DocumentsBadge, StatusBadge, vehicleTitle } from '@/features/vehicles/parts';
import { useConfirm } from '@/components/ConfirmDialog';
import { useDir, useI18n } from '@/i18n';
import { api, ApiError, type Vehicle } from '@/lib/api';
import { dateShort, time } from '@/lib/format';
import { useLabel } from '@/lib/useLabel';

export function VehicleDetailPage() {
  const { t } = useI18n();
  const { forward } = useDir();
  const confirm = useConfirm();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, role } = usePermissions();
  const canWrite = can('vehicles', 'write');

  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [returning, setReturning] = useState(false);

  const query = useQuery({ queryKey: ['vehicle', id], queryFn: () => api.vehicle(id) });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['vehicle', id] });
    await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
  };

  const setStatus = useMutation({
    mutationFn: (status: 'available' | 'maintenance' | 'retired') => api.updateVehicle(id, { status }),
    onSettled: refresh,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteVehicle(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      navigate('/vehicles');
    },
  });

  if (query.isLoading) return <Spinner />;
  if (query.error) return <ErrorNotice error={query.error} />;
  if (!query.data) return null;

  const { vehicle: v, history } = query.data;
  const actionError = setStatus.error ?? remove.error;

  return (
    <>
      <PageHeader
        title={v.plate}
        subtitle={vehicleTitle(v)}
        actions={
          <>
            <Link to="/vehicles" className="btn-secondary">
              {t('common.backToList')}
            </Link>
            {canWrite ? (
              <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
                {t('common.edit')}
              </button>
            ) : null}
            {canWrite && role === 'admin' && !v.holder ? (
              <button
                type="button"
                className="btn-danger"
                disabled={remove.isPending}
                onClick={async () => {
                  if (await confirm({ message: t('vehicles.deleteConfirm', { plate: v.plate }), confirmLabel: t('common.delete'), danger: true })) {
                    remove.mutate();
                  }
                }}
              >
                {t('common.delete')}
              </button>
            ) : null}
          </>
        }
      />

      <VehicleForm open={editing} onClose={() => setEditing(false)} vehicle={v} />
      <AssignDialog vehicle={v} open={assigning} onClose={() => setAssigning(false)} onDone={refresh} />
      <ReturnDialog vehicle={v} open={returning} onClose={() => setReturning(false)} onDone={refresh} />

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <StatusBadge status={v.status} />
        <DocumentsBadge vehicle={v} />
      </div>

      {actionError ? (
        <p role="alert" className="mb-4 text-sm text-danger">
          {actionError instanceof ApiError ? actionError.message : t('common.saveError')}
        </p>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <section>
            <h2 className="font-bold mb-3">{t('vehicles.documentsTitle')}</h2>
            <RegisterPanel scope="vehicle" vehicleId={v.id} />
          </section>

          <section>
            <h2 className="font-bold mb-3">{t('vehicles.historyTitle')}</h2>
            <Card padded={false}>
              {history.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted">{t('vehicles.noHistory')}</div>
              ) : (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th className="th">{t('vehicles.driver')}</th>
                        <th className="th">{t('vehicles.from')}</th>
                        <th className="th">{t('vehicles.to')}</th>
                        <th className="th">{t('vehicles.km')}</th>
                        <th className="th">{t('vehicles.notes')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id}>
                          <td className="td">
                            <Link to={`/employees/${h.userId}`} className="font-semibold hover:text-primary hover:underline">
                              {h.userName}
                            </Link>
                          </td>
                          <td className="td tabular text-sm whitespace-nowrap">
                            {dateShort(h.assignedAt)} {time(h.assignedAt)}
                            {h.assignedByName ? (
                              <span className="block text-xs text-subtle">{h.assignedByName}</span>
                            ) : null}
                          </td>
                          <td className="td tabular text-sm whitespace-nowrap">
                            {h.returnedAt ? (
                              <>
                                {dateShort(h.returnedAt)} {time(h.returnedAt)}
                                {h.returnedToName ? (
                                  <span className="block text-xs text-subtle">{h.returnedToName}</span>
                                ) : null}
                              </>
                            ) : (
                              <span className="font-semibold text-primary">{t('vehicles.inUse')}</span>
                            )}
                          </td>
                          <td className="td tabular text-sm text-muted whitespace-nowrap">
                            {h.odometerOut !== null ? h.odometerOut.toLocaleString('it-IT') : '—'}
                            {` ${forward} `}
                            {h.odometerIn !== null ? h.odometerIn.toLocaleString('it-IT') : '…'}
                            {h.odometerOut !== null && h.odometerIn !== null ? (
                              <span className="block text-xs">
                                +{(h.odometerIn - h.odometerOut).toLocaleString('it-IT')} km
                              </span>
                            ) : null}
                          </td>
                          <td className="td text-xs text-muted">
                            {[h.notesOut, h.notesIn].filter(Boolean).join(' · ') || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </section>
        </div>

        <div className="space-y-6">
          {/* Who has it: the first question anyone opening this page has. */}
          <section>
            <h2 className="font-bold mb-3">{t('vehicles.holder')}</h2>
            <Card>
              {v.holder ? (
                <div className="space-y-1">
                  <Link
                    to={`/employees/${v.holder.userId}`}
                    className="text-base font-semibold hover:text-primary hover:underline"
                  >
                    {v.holder.name}
                  </Link>
                  {v.holder.since ? (
                    <p className="text-xs text-muted">{t('vehicles.since', { date: dateShort(v.holder.since) })}</p>
                  ) : null}
                  {v.holder.dueBackOn ? (
                    <p className="text-xs text-muted">
                      {t('vehicles.dueBack', { date: dateShort(v.holder.dueBackOn) })}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted">{t('vehicles.nobody')}</p>
              )}
              {canWrite && v.status !== 'retired' ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" className="btn-primary" onClick={() => setAssigning(true)}>
                    {v.holder ? t('vehicles.handOver') : t('vehicles.assign')}
                  </button>
                  {v.holder ? (
                    <button type="button" className="btn-secondary" onClick={() => setReturning(true)}>
                      {t('vehicles.return')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </Card>
          </section>

          <section>
            <h2 className="font-bold mb-3">{t('vehicles.details')}</h2>
            <Card>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <Detail label={t('vehicles.plate')} value={<span className="font-mono">{v.plate}</span>} />
                <Detail label={t('vehicles.name')} value={v.name} />
                <Detail label={t('vehicles.make')} value={v.make} />
                <Detail label={t('vehicles.model')} value={v.model} />
                <Detail label={t('vehicles.year')} value={v.year} />
                <Detail label={t('vehicles.fuel')} value={v.fuel ? t(`vehicles.fuels.${v.fuel}`) : null} />
                <Detail label={t('vehicles.registeredOn')} value={v.registeredOn ? dateShort(v.registeredOn) : null} />
                <Detail
                  label={t('vehicles.km')}
                  value={v.odometerKm !== null ? `${v.odometerKm.toLocaleString('it-IT')} km` : null}
                />
                <Detail label={t('vehicles.vin')} value={v.vin ? <span className="font-mono text-xs">{v.vin}</span> : null} />
              </dl>
              {v.notes ? <p className="mt-3 text-sm text-muted whitespace-pre-line">{v.notes}</p> : null}

              {/* Off the road or back on it. Only while nobody is driving it. */}
              {canWrite && !v.holder ? (
                <label className="mt-4 block">
                  <span className="mb-1.5 block text-xs font-semibold text-muted">{t('common.status')}</span>
                  <select
                    className="input"
                    value={v.status}
                    disabled={setStatus.isPending}
                    onChange={(e) => setStatus.mutate(e.target.value as 'available' | 'maintenance' | 'retired')}
                  >
                    {(['available', 'maintenance', 'retired'] as const).map((s) => (
                      <option key={s} value={s}>
                        {t(`vehicles.status.${s}`)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </Card>
          </section>
        </div>
      </div>
    </>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="text-end font-medium">{value === null || value === undefined || value === '' ? '—' : value}</dd>
    </>
  );
}

function AssignDialog({
  vehicle,
  open,
  onClose,
  onDone,
}: {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useI18n();
  const L = useLabel();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users(), enabled: open });
  const [userId, setUserId] = useState('');
  const [km, setKm] = useState('');
  const [dueBackOn, setDueBackOn] = useState('');
  const [notes, setNotes] = useState('');

  const assign = useMutation({
    mutationFn: () =>
      api.assignVehicle(vehicle.id, {
        userId,
        ...(km ? { odometerKm: Number(km) } : {}),
        ...(dueBackOn ? { dueBackOn } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: async () => {
      setUserId('');
      setKm('');
      setDueBackOn('');
      setNotes('');
      onClose();
      await onDone();
    },
  });

  const people = (users.data?.users ?? []).filter(
    (u) => u.status !== 'suspended' && u.id !== vehicle.holder?.userId,
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={
        vehicle.holder
          ? t('vehicles.handOverTitle', { plate: vehicle.plate, name: vehicle.holder.name ?? '' })
          : t('vehicles.assignTitle', { plate: vehicle.plate })
      }
      description={vehicle.holder ? t('vehicles.handOverHint') : undefined}
      footer={
        <>
          <button
            type="button"
            className="btn-primary"
            disabled={!userId || assign.isPending}
            onClick={() => assign.mutate()}
          >
            {assign.isPending ? t('common.saving') : t('common.confirm')}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('vehicles.driver')}>
          <Combobox value={userId} onChange={setUserId} options={personOptions(people, L)} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('vehicles.kmNow')} hint={t('common.optional')}>
            <input
              className="input"
              inputMode="numeric"
              value={km}
              placeholder={vehicle.odometerKm !== null ? String(vehicle.odometerKm) : ''}
              onChange={(e) => setKm(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
          <Field label={t('vehicles.dueBackOn')} hint={t('common.optional')}>
            <input className="input" type="date" value={dueBackOn} onChange={(e) => setDueBackOn(e.target.value)} />
          </Field>
        </div>
        <Field label={t('vehicles.notes')} hint={t('common.optional')}>
          <input
            className="input"
            maxLength={2000}
            value={notes}
            placeholder={t('vehicles.assignNotesPlaceholder')}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        {assign.error ? (
          <p role="alert" className="text-sm text-danger">
            {assign.error instanceof ApiError ? assign.error.message : t('common.saveError')}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

function ReturnDialog({
  vehicle,
  open,
  onClose,
  onDone,
}: {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [km, setKm] = useState('');
  const [notes, setNotes] = useState('');
  const [toGarage, setToGarage] = useState(false);

  const back = useMutation({
    mutationFn: () =>
      api.returnVehicle(vehicle.id, {
        status: toGarage ? 'maintenance' : 'available',
        ...(km ? { odometerKm: Number(km) } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: async () => {
      setKm('');
      setNotes('');
      setToGarage(false);
      onClose();
      await onDone();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={t('vehicles.returnTitle', { plate: vehicle.plate, name: vehicle.holder?.name ?? '' })}
      footer={
        <>
          <button type="button" className="btn-primary" disabled={back.isPending} onClick={() => back.mutate()}>
            {back.isPending ? t('common.saving') : t('vehicles.return')}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('vehicles.kmNow')} hint={t('vehicles.kmHint')}>
          <input
            className="input"
            inputMode="numeric"
            autoFocus
            value={km}
            placeholder={vehicle.odometerKm !== null ? String(vehicle.odometerKm) : ''}
            onChange={(e) => setKm(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        <Field label={t('vehicles.notes')} hint={t('common.optional')}>
          <input
            className="input"
            maxLength={2000}
            value={notes}
            placeholder={t('vehicles.returnNotesPlaceholder')}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={toGarage} onChange={(e) => setToGarage(e.target.checked)} />
          {t('vehicles.toGarage')}
        </label>
        {back.error ? (
          <p role="alert" className="text-sm text-danger">
            {back.error instanceof ApiError ? back.error.message : t('common.saveError')}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
